import {z} from 'zod';
import {requisitionDate} from './requisitions';
import {CLAIM_SEVERITIES,ISSUE_CODES,ROOT_CAUSES,claimCurrencySchema,claimLineInputSchema,normalizeClaims,resolveSeverity,type ClaimLine,type ClaimSeverity,type RootCause} from './claims';
export const serviceTicketKindSchema=z.enum(['Ticket','RMA']);
export const serviceTicketDraftSchema=z.object({
 requestId:z.string().uuid(),kind:serviceTicketKindSchema,customerId:z.string().min(1).max(100),
 dnId:z.string().min(1).max(150).nullable().default(null),
 /**
  * Nguồn thay thế khi chưa có phiếu giao — khách yêu cầu 14/09/2026.
  *
  * Hệ có 0 phiếu giao, nên ô "Delivery note" luôn rỗng và cả tính năng hậu mãi
  * không dùng được. Hồ sơ khiếu nại chỉ GHI NHẬN, không động vào kho và không
  * sinh bút toán, nên bất cứ chứng từ nào định danh được khách + mã hàng + số
  * lượng đều đủ làm nguồn.
  */
 piId:z.string().min(1).max(150).nullable().default(null),
 invoiceId:z.string().min(1).max(150).nullable().default(null),
 subject:z.string().trim().min(3).max(200),
 priority:z.enum(['Low','Medium','High','Urgent']).default('Medium'),notes:z.string().trim().max(2000).default(''),
 /**
  * Hạn trả lời khách (19/09/2026, TKT-R02 chốt theo chuẩn helpdesk / SAP QM "ngày cần xong"): người xử lý tự đặt, không bắt buộc.
  * Hệ KHÔNG tự tính theo mức ưu tiên — thời hạn cam kết với khách là chính sách của Hisense, không bịa. Để trống = chưa hẹn.
  */
 dueDate:z.union([z.literal(''),requisitionDate]).default(''),
 /**
  * Phân loại khiếu nại theo danh mục khách gửi 17/09/2026 (claims.ts): loại lỗi,
  * mức độ, khách đòi gì, và NGUYÊN NHÂN. Nguyên nhân "Supplier" là cửa để lập hồ
  * sơ khiếu nại nhà cung cấp — Louis: lỗi do vật liệu thì phạt nhà cung cấp.
  */
 issueCode:z.enum(ISSUE_CODES),
 severity:z.enum(CLAIM_SEVERITIES).nullable().default(null),
 claims:z.array(claimLineInputSchema).max(12).default([]),
 claimCurrency:claimCurrencySchema,
 rootCause:z.enum(ROOT_CAUSES).default('Undetermined'),
 rootCauseNote:z.string().trim().max(2000).default(''),
}).superRefine((v,ctx)=>{
 /** Hai nguồn cùng lúc thì không ai biết hồ sơ này nói về chuyến hàng nào. */
 if([v.dnId,v.piId,v.invoiceId].filter(Boolean).length>1)ctx.addIssue({code:z.ZodIssueCode.custom,message:'ticket_source_single',path:['piId']});
 if(!resolveSeverity(v.issueCode,v.severity))ctx.addIssue({code:z.ZodIssueCode.custom,message:'claim_severity',path:['severity']});
 const claims=normalizeClaims(v.issueCode,'customer',v.claims,v.claimCurrency);
 if(!claims.ok)ctx.addIssue({code:z.ZodIssueCode.custom,message:claims.error,path:['claims']});
 /** Kết luận nguyên nhân mà không ghi căn cứ thì không ai kiểm lại được — nhất là khi nó dẫn tới phạt nhà cung cấp. */
 if(v.rootCause!=='Undetermined'&&v.rootCauseNote.length<5)ctx.addIssue({code:z.ZodIssueCode.custom,message:'root_cause_note',path:['rootCauseNote']});
});
export const serviceTicketUpdateSchema=serviceTicketDraftSchema.and(
 z.object({revision:z.number().int().positive(),reason:z.string().trim().min(5).max(1000)}));
export const ticketFinancialReferenceSchema=z.object({kind:z.enum(['Invoice','ExternalCreditNote','ExternalRefund','Other']),reference:z.string().trim().min(1).max(200),documentId:z.string().min(1).max(150).nullable().default(null)}).refine(v=>v.kind!=='Invoice'||!!v.documentId);
export const ticketResolutionSchema=z.object({notes:z.string().trim().min(5).max(2000),financialReferences:z.array(ticketFinancialReferenceSchema).max(20).default([])});
export const ticketArchiveSchema=z.object({revision:z.number().int().positive(),reason:z.string().trim().min(5).max(1000)});
export type ServiceTicketUpdate=z.input<typeof serviceTicketUpdateSchema>;
export type TicketResolution=z.infer<typeof ticketResolutionSchema>;
export const serviceTicketActionSchema=z.object({
 revision:z.number().int().positive(),action:z.enum(['start','wait','resolve','reopen','close','cancel','assign','linkReturn']),
 reason:z.string().trim().min(5).max(1000),assigneeId:z.string().min(1).max(100).nullable().default(null),
 returnId:z.string().uuid().nullable().default(null),resolution:ticketResolutionSchema.optional(),
});
export type ServiceTicketInput=z.input<typeof serviceTicketDraftSchema>;
export type ServiceTicketAction=z.input<typeof serviceTicketActionSchema>;
/** `dueDate`: hạn trả lời khách (YYYY-MM-DD), vắng = chưa hẹn. `resolution`: kết quả lần xử lý gần nhất — mở lại thì giữ, xử lý lại thì thay. */
export interface ServiceTicketDefinition {notes:string;dueDate?:string;resolution?:TicketResolution;}
export interface ServiceTicketDto {
 issueCode:string|null;severity:ClaimSeverity|null;claims:ClaimLine[];claimCurrency:string|null;rootCause:RootCause;rootCauseNote:string;
 id:string;ticketNo:string;kind:'Ticket'|'RMA';customerId:string;dnId:string|null;piId:string|null;invoiceId:string|null;returnId:string|null;
 assigneeId:string|null;subject:string;priority:'Low'|'Medium'|'High'|'Urgent';
 status:'Open'|'InProgress'|'Waiting'|'Resolved'|'Closed'|'Cancelled';revision:number;definition:ServiceTicketDefinition;
 createdAt:number;createdBy:string|null;archived:boolean;customerName:string;dnNo:string|null;piNo:string|null;invoiceNo:string|null;assigneeName:string|null;returnNo:string|null;returnStatus:string|null;events:{action:string;reason:string;createdAt:number;createdBy:string|null;snapshot:string|null}[];
}
